import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { 
  User, FileText, Clock, CheckCircle2, XCircle, AlertTriangle, 
  TrendingDown, Activity, Pill, Calendar, Video, MessageSquare,
  ChevronRight, RefreshCw, Star, Heart, Building, Stethoscope,
  Send, Eye, ThumbsUp
} from 'lucide-react';
import { useToast } from "../hooks/use-toast";

const API = process.env.REACT_APP_BACKEND_URL;

const CollaboratingPhysicianPortal = () => {
  const [patients, setPatients] = useState([]);
  const [pendingCosigns, setPendingCosigns] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('patients');
  const { toast } = useToast();

  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch my patients
      const patientsRes = await fetch(`${API}/api/phase38-44/physician/my-patients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (patientsRes.ok) {
        const data = await patientsRes.json();
        setPatients(data);
      }

      // Fetch pending co-signatures
      const cosignsRes = await fetch(`${API}/api/phase38-44/physician/pending-cosigns`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (cosignsRes.ok) {
        const data = await cosignsRes.json();
        setPendingCosigns(data);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch AI Snapshot for a patient
  const fetchSnapshot = async (patientId) => {
    try {
      const res = await fetch(`${API}/api/phase38-44/physician/snapshot/${patientId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSnapshot(data);
      }
    } catch (err) {
      console.error('Error fetching snapshot:', err);
    }
  };

  // Process co-signature
  const processCosign = async (requestId, action, notes = '') => {
    try {
      const res = await fetch(`${API}/api/phase38-44/physician/cosign/${requestId}?action=${action}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notes })
      });

      if (res.ok) {
        toast({
          title: action === 'approve' ? "Order Co-Signed" : "Order Declined",
          description: action === 'approve' ? 
            "The order has been approved with your co-signature." :
            "The hospice team has been notified."
        });
        fetchData();
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to process co-signature", variant: "destructive" });
    }
  };

  const selectPatient = (patient) => {
    setSelectedPatient(patient);
    fetchSnapshot(patient.id);
    setActiveTab('snapshot');
  };

  // Weekly 30-Second Update
  const WeeklyDigest = ({ patient, snapshot }) => (
    <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg text-blue-900">30-Second Weekly Update</CardTitle>
          <Badge variant="outline" className="border-blue-300 text-blue-700">
            {snapshot?.period_start} - {snapshot?.period_end}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* 3-Point Summary */}
        <div className="space-y-3">
          {/* 1. Comfort Level */}
          <div className="flex items-start gap-3 p-3 bg-white rounded-lg">
            <div className="bg-green-100 p-2 rounded-full">
              <Heart className="h-5 w-5 text-green-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-gray-800">Current Comfort Level</h4>
              <p className="text-sm text-gray-600">
                {snapshot?.summary?.comfort_level?.level || 'Comfortable'} - 
                Pain {snapshot?.summary?.comfort_level?.pain_controlled ? 'Controlled' : 'Needs Attention'}
              </p>
            </div>
            <Badge className={
              snapshot?.summary?.overall_status === 'stable' ? 'bg-green-500' :
              snapshot?.summary?.overall_status === 'declining' ? 'bg-orange-500' : 'bg-yellow-500'
            }>
              {snapshot?.summary?.overall_status?.replace('_', ' ') || 'Stable'}
            </Badge>
          </div>

          {/* 2. Medication Changes */}
          <div className="flex items-start gap-3 p-3 bg-white rounded-lg">
            <div className="bg-blue-100 p-2 rounded-full">
              <Pill className="h-5 w-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-gray-800">Medication Changes This Week</h4>
              <p className="text-sm text-gray-600">
                {snapshot?.summary?.medication_changes || 0} changes made by hospice team
              </p>
            </div>
          </div>

          {/* 3. Memory Note */}
          <div className="flex items-start gap-3 p-3 bg-white rounded-lg">
            <div className="bg-purple-100 p-2 rounded-full">
              <Star className="h-5 w-5 text-purple-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-gray-800">Memory Note</h4>
              <p className="text-sm text-gray-600 italic">
                "{patient?.name || 'Patient'} mentioned they appreciate your continued involvement in their care."
              </p>
            </div>
          </div>
        </div>

        {/* Acknowledge Button */}
        <Button 
          className="w-full bg-blue-600 hover:bg-blue-700"
          onClick={() => toast({ 
            title: "Update Acknowledged", 
            description: "Your acknowledgment has been recorded for compliance." 
          })}
        >
          <ThumbsUp className="h-4 w-4 mr-2" />
          Acknowledge Update
        </Button>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Stethoscope className="h-7 w-7 text-teal-600" />
            Collaborating Physician Portal
          </h1>
          <p className="text-gray-600">Welcome, Dr. {user.name || 'Physician'}</p>
        </div>
        <div className="flex gap-2">
          <Badge variant="outline" className="text-lg px-4 py-2">
            {patients.length} Patients
          </Badge>
          {pendingCosigns.length > 0 && (
            <Badge className="bg-orange-500 text-lg px-4 py-2">
              {pendingCosigns.length} Pending Actions
            </Badge>
          )}
        </div>
      </div>

      {/* Pending Co-Signatures Alert */}
      {pendingCosigns.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-orange-500" />
              <div className="flex-1">
                <h3 className="font-semibold text-orange-900">Orders Awaiting Co-Signature</h3>
                <p className="text-sm text-orange-700">
                  {pendingCosigns.length} order(s) require your review and co-signature
                </p>
              </div>
              <Button 
                variant="outline" 
                className="border-orange-400 text-orange-700 hover:bg-orange-100"
                onClick={() => setActiveTab('cosigns')}
              >
                Review Now
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="patients">My Patients</TabsTrigger>
          <TabsTrigger value="snapshot">
            Snapshot
            {selectedPatient && <span className="ml-1 text-xs">({selectedPatient.name?.split(' ')[0]})</span>}
          </TabsTrigger>
          <TabsTrigger value="cosigns">
            Co-Signs
            {pendingCosigns.length > 0 && (
              <Badge className="ml-1 bg-orange-500 h-5 w-5 p-0 justify-center">
                {pendingCosigns.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* My Patients */}
        <TabsContent value="patients" className="space-y-4">
          {patients.length > 0 ? (
            <div className="grid gap-4">
              {patients.map((patient, i) => (
                <Card 
                  key={i} 
                  className="cursor-pointer hover:border-teal-300 transition-colors"
                  onClick={() => selectPatient(patient)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="bg-teal-100 p-3 rounded-full">
                          <User className="h-6 w-6 text-teal-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">{patient.name}</h3>
                          <p className="text-sm text-gray-600">
                            {patient.clinical?.primary_diagnosis || 'Hospice Patient'}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline">
                              {patient.collaboration?.access_level || 'co_signer'}
                            </Badge>
                            <span className="text-xs text-gray-500">
                              Admitted: {patient.admission_date?.split('T')[0] || 'N/A'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={(e) => {
                          e.stopPropagation();
                          // Open video call
                        }}>
                          <Video className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={(e) => {
                          e.stopPropagation();
                          // Open messaging
                        }}>
                          <MessageSquare className="h-4 w-4" />
                        </Button>
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <User className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <h3 className="font-semibold text-gray-600">No Patients Yet</h3>
                <p className="text-sm text-gray-500">
                  Your patients will appear here once you're connected as a collaborating physician.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* AI Snapshot */}
        <TabsContent value="snapshot" className="space-y-4">
          {selectedPatient ? (
            <>
              {/* Weekly Digest Card */}
              <WeeklyDigest patient={selectedPatient} snapshot={snapshot} />

              {/* Clinical Highlights */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Clinical Highlights</CardTitle>
                  <CardDescription>
                    {snapshot?.summary?.visit_count || 0} visits this reporting period
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {snapshot?.clinical_highlights?.length > 0 ? (
                    <ul className="space-y-2">
                      {snapshot.clinical_highlights.map((highlight, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-500 mt-1" />
                          <span className="text-gray-700">{highlight}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-500">No highlights for this period</p>
                  )}
                </CardContent>
              </Card>

              {/* Trends */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Activity className="h-5 w-5" />
                    Clinical Trends
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">PPS Trend</p>
                      <div className="flex items-center gap-2">
                        {snapshot?.trends?.pps === 'declining' ? (
                          <TrendingDown className="h-5 w-5 text-red-500" />
                        ) : (
                          <Activity className="h-5 w-5 text-green-500" />
                        )}
                        <span className="font-semibold capitalize">
                          {snapshot?.trends?.pps || 'Stable'}
                        </span>
                      </div>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">Functional Status</p>
                      <div className="flex items-center gap-2">
                        <Activity className="h-5 w-5 text-blue-500" />
                        <span className="font-semibold capitalize">
                          {snapshot?.trends?.functional_status || 'Stable'}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Care Team Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Care Team Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700">
                    {snapshot?.care_team_summary || 'No significant notes this period.'}
                  </p>
                </CardContent>
              </Card>

              {/* Recommendations */}
              {snapshot?.recommendations?.length > 0 && (
                <Card className="border-teal-200 bg-teal-50">
                  <CardHeader>
                    <CardTitle className="text-lg text-teal-900">Recommendations</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {snapshot.recommendations.map((rec, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <Star className="h-4 w-4 text-teal-600 mt-1" />
                          <span className="text-teal-800">{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <Eye className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <h3 className="font-semibold text-gray-600">Select a Patient</h3>
                <p className="text-sm text-gray-500">
                  Choose a patient from the list to view their AI-generated snapshot report.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Pending Co-Signatures */}
        <TabsContent value="cosigns" className="space-y-4">
          {pendingCosigns.length > 0 ? (
            <div className="space-y-4">
              {pendingCosigns.map((request, i) => (
                <Card key={i} className="border-orange-200">
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-orange-500">{request.order_type}</Badge>
                          <span className="text-sm text-gray-500">
                            {new Date(request.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <h3 className="font-semibold">{request.patient_name}</h3>
                        <p className="text-gray-700">{request.order_description}</p>
                        <p className="text-sm text-gray-500 mt-1">
                          Ordered by: {request.hospice_physician}
                        </p>
                      </div>
                      <div className="flex gap-2 self-end md:self-center">
                        <Button 
                          className="bg-green-500 hover:bg-green-600"
                          onClick={() => processCosign(request.id, 'approve')}
                        >
                          <CheckCircle2 className="h-4 w-4 mr-2" />
                          Approve
                        </Button>
                        <Button 
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50"
                          onClick={() => processCosign(request.id, 'decline')}
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Decline
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <CheckCircle2 className="h-12 w-12 mx-auto text-green-400 mb-3" />
                <h3 className="font-semibold text-gray-600">All Caught Up!</h3>
                <p className="text-sm text-gray-500">
                  No pending co-signature requests at this time.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CollaboratingPhysicianPortal;
